import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router';
import { Check } from 'lucide-react';
import { saveProgress, setCurrentStep, loadApplicationData, restartApplication } from '../store/loanApplicationSlice';
import ProductRecommendationFlow from '../components/LoanApplication/ProductRecommendationFlow';
import URLAApplication from '../components/LoanApplication/URLAApplication';
import DocumentPreparation from '../components/LoanApplication/DocumentPreparation';
import OwenUnderwritingVerdict from '../components/LoanApplication/OwenUnderwritingVerdict';
import HumanUnderwritingFlow from '../components/LoanApplication/HumanUnderwritingFlow';
import LenderNetworkSelection from '../components/LoanApplication/LenderNetworkSelection';
import LenderSubmission from '../components/LoanApplication/LenderSubmission';
import SubmitToUnderwriter from '../components/LoanApplication/SubmitToUnderwriter';
import WelcomeCheck from '../components/LoanApplication/WelcomeCheck';
import ComplianceFooter from '../components/common/ComplianceFooter';
import FloatingOwenAssistant from '../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../components/common/FloatingKnowledge';
import ApplyHeader from '../components/layout/ApplyHeader';
import { LK_FEATURES } from '../config/features';
import { STEPS } from '../store/loanApplicationSlice';
import { supabase } from '../lib/supabase';

// Map a numeric currentStep to a context label for FloatingOwenAssistant /
// FloatingKnowledge content lookup.
const contextForStep = (step) => {
  if (step === STEPS.ASSESSMENT) return 'assessment';
  if (step === STEPS.APPLICATION) return 'application';
  if (step === STEPS.DOCUMENTS) return 'documents';
  if (step === STEPS.UNDERWRITING_PICKER || step === STEPS.UNDERWRITING || step === STEPS.HUMAN_UNDERWRITING) {
    return 'underwriting';
  }
  return 'submit';
};

const LoanApplicationPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentStep, lastSaved, recommendedProduct, productRecommendation, applicationData, isAuthenticated, user } = useSelector(
    (state) => state.loanApplication
  );

  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [applicationSessionKey, setApplicationSessionKey] = useState(0);
  const [currentApplicationId, setCurrentApplicationId] = useState(null);
  const appIdRef = useRef(null);

  // Keep ref in sync so async functions always have latest value
  useEffect(() => { appIdRef.current = currentApplicationId; }, [currentApplicationId]);

  // Check auth and load application on mount
  useEffect(() => {
    checkAuthAndLoadApplication();
  }, []);

  const checkAuthAndLoadApplication = async () => {
    try {
      // First check localStorage for mock auth session
      const localSession = localStorage.getItem('lk_user_session');
      if (localSession) {
        const userData = JSON.parse(localSession);
        dispatch({ type: 'loanApplication/setUser', payload: userData });
      }
      
      // Then check Supabase auth
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const userData = {
          id: session.user.id,
          email: session.user.email,
          firstName: session.user.user_metadata?.firstName || session.user.email.split('@')[0],
        };
        dispatch({ type: 'loanApplication/setUser', payload: userData });

        const resumeId = searchParams.get('resume');
        if (resumeId) {
          await loadApplication(resumeId);
        }
      }

      const resumeId = searchParams.get('resume');
      if (resumeId && !session) {
        navigate(`/borrower/login?redirect=${encodeURIComponent(`/apply?resume=${resumeId}`)}`, { replace: true });
      }
    } catch (error) {
      console.error('Auth check error:', error);
    }
  };

  const loadApplication = async (appId) => {
    try {
      const { data, error } = await supabase
        .from('loan_applications')
        .select('*')
        .eq('id', appId)
        .single();

      if (error) throw error;
      if (data) {
        setCurrentApplicationId(appId);
        dispatch(loadApplicationData(data));
      }
    } catch (error) {
      console.error('Error loading application:', error);
    }
  };

  // Save immediately when user becomes authenticated, then auto-save every 2 minutes
  useEffect(() => {
    if (!isAuthenticated) return;

    // Immediate save on auth detection
    handleSaveProgress();

    const interval = setInterval(() => {
      handleSaveProgress();
    }, 120000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleSaveProgress = async () => {
    if (!isAuthenticated || !user) return;

    try {
      const applicationPayload = {
        applicant_id: user.id,
        status: currentStep === STEPS.ASSESSMENT ? 'draft' : 'in_progress',
        current_step: currentStep,
        assessment_answers: productRecommendation || {},
        borrower_info: applicationData.personalInfo || {},
        property_type: applicationData.property?.propertyType || null,
        property_use: applicationData.property?.occupancy || null,
        property_address: applicationData.property || {},
        purchase_price: parseFloat(applicationData.loan?.purchasePrice) || null,
        down_payment: parseFloat(applicationData.loan?.downPayment) || null,
        loan_amount: parseFloat(applicationData.loan?.loanAmount) || null,
        employment_info: applicationData.employment || {},
        income_monthly: parseFloat(applicationData.income?.monthlyIncome) || null,
        income_breakdown: applicationData.income || {},
        assets_info: applicationData.assets || {},
        credit_score: parseInt(applicationData.personalInfo?.creditScore) || null,
        updated_at: new Date().toISOString(),
      };

      if (currentApplicationId) {
        // Update existing application
        const { error } = await supabase
          .from('loan_applications')
          .update(applicationPayload)
          .eq('id', currentApplicationId);

        if (error) throw error;
      } else {
        // Create new application
        const { data, error } = await supabase
          .from('loan_applications')
          .insert([applicationPayload])
          .select()
          .single();

        if (error) throw error;
        if (data) setCurrentApplicationId(data.id);
      }

      dispatch(saveProgress());
      setShowSaveNotification(true);
      setTimeout(() => setShowSaveNotification(false), 3000);
    } catch (error) {
      console.error('Error saving application:', error);
    }
  };

  const handleLoginRedirect = () => {
    // Save current state to session storage before redirecting
    sessionStorage.setItem('lk_application_draft', JSON.stringify({
      currentStep,
      recommendedProduct,
      productRecommendation,
      applicationData,
    }));
    navigate('/borrower/login?redirect=/apply');
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('lk_user_session');
    dispatch({ type: 'loanApplication/logout' });
    setCurrentApplicationId(null);
  };

  const handleStartOver = () => {
    dispatch(restartApplication());
    setCurrentApplicationId(null);
    sessionStorage.removeItem('lk_application_draft');
    setApplicationSessionKey((k) => k + 1);
    navigate('/apply', { replace: true });
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case STEPS.ASSESSMENT:
        return <ProductRecommendationFlow />;
      case STEPS.APPLICATION:
        return recommendedProduct ? <URLAApplication /> : <ProductRecommendationFlow />;
      case STEPS.DOCUMENTS:
        return <DocumentPreparation />;
      case STEPS.UNDERWRITING_PICKER:
        // MVP: skip AI-vs-human picker; route directly to Owen's verdict.
        return LK_FEATURES.humanUnderwriter ? <SubmitToUnderwriter /> : <OwenUnderwritingVerdict />;
      case STEPS.UNDERWRITING:
        return <OwenUnderwritingVerdict />;
      case STEPS.HUMAN_UNDERWRITING:
        return LK_FEATURES.humanUnderwriter ? <HumanUnderwritingFlow /> : <OwenUnderwritingVerdict />;
      case STEPS.LENDER_NETWORK:
        // MVP: lender marketplace gated. Owen's verdict ends the borrower journey.
        return LK_FEATURES.lenderMarketplace ? <LenderNetworkSelection /> : <OwenUnderwritingVerdict />;
      case STEPS.LENDER_SUBMISSION:
        return LK_FEATURES.lenderMarketplace ? <LenderSubmission /> : <OwenUnderwritingVerdict />;
      default:
        return <ProductRecommendationFlow />;
    }
  };

  const isConversationalAssessment = currentStep === STEPS.ASSESSMENT;
  const useFullWidth = currentStep === STEPS.APPLICATION;

  if (isConversationalAssessment) {
    return (
      <div key={applicationSessionKey}>
        <ProductRecommendationFlow />
        {showSaveNotification && (
          <div style={{
            position: 'fixed', bottom: 'var(--lk-space-4)', right: 'var(--lk-space-4)',
            backgroundColor: 'var(--lk-success-600)', color: 'white',
            padding: 'var(--lk-space-3) var(--lk-space-4)', borderRadius: 'var(--lk-radius-lg)',
            boxShadow: 'var(--lk-shadow-lg)', zIndex: 2000,
            display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)',
            fontSize: 'var(--lk-text-sm)', fontWeight: 500,
          }}>
            <Check size={16} />
            Progress saved
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'var(--lk-bg-page)' }}>
      <ApplyHeader
        currentStep={currentStep}
        onStepClick={(stageKey) => {
          const target = STEPS[stageKey];
          if (typeof target === 'number') dispatch(setCurrentStep(target));
        }}
        onSaveNow={handleSaveProgress}
        onSignIn={handleLoginRedirect}
        onSignOut={handleLogout}
        onStartOver={handleStartOver}
        lastSaved={lastSaved}
      />

      <WelcomeCheck />

      <div style={{ flex: 1 }}>
        <div style={{
          maxWidth: useFullWidth ? '100%' : 1200,
          margin: '0 auto',
          padding: 'var(--lk-space-6) var(--lk-space-4)',
        }}>
          <div key={applicationSessionKey}>
            {renderCurrentStep()}
          </div>
        </div>
      </div>

      {showSaveNotification && (
        <div style={{
          position: 'fixed', bottom: 'var(--lk-space-4)', right: 'var(--lk-space-4)',
          backgroundColor: 'var(--lk-success-600)', color: 'white',
          padding: 'var(--lk-space-3) var(--lk-space-4)', borderRadius: 'var(--lk-radius-lg)',
          boxShadow: 'var(--lk-shadow-lg)', zIndex: 2000,
          display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)',
          fontSize: 'var(--lk-text-sm)', fontWeight: 500,
        }}>
          <Check size={16} />
          Progress saved
        </div>
      )}

      <ComplianceFooter />

      <FloatingOwenAssistant
        context={contextForStep(currentStep)}
        contextData={{ currentStep }}
      />
      <FloatingKnowledge context={contextForStep(currentStep)} />
    </div>
  );
};

export default LoanApplicationPage;
